<template>
  <header
    ref="headerRef"
    class="w-full bg-[#041E42] text-gray-50 shadow-sm sticky top-0 z-40 transition-transform duration-300 ease-in-out"
    :style="{ transform: showTopBar ? 'translateY(0)' : `translateY(-${topBarHeight}px)` }"
  >
    <div ref="topBarRef" class="hidden lg:block bg-[#11294c] text-sm text-gray-300">
      <div class="max-w-7xl mx-auto py-2 flex items-center justify-between">
        <div class="flex items-center gap-6">
          <a href="mailto:webenquiries@gmgautomation.net" class="flex items-center gap-4 link">
            <i class="pi pi-envelope text-lg"></i>
            <span class="text-xs">webenquiries@gmgautomation.net</span>
          </a>
          <a href="tel:+390409871972" class="flex items-center gap-2 link">
            <i class="pi pi-phone text-lg"></i>
            <span class="text-xs">{{ t('header.phone') }}</span>
          </a>
        </div>

        <div class="flex items-center gap-10 ml-auto">
          <div class="flex items-center gap-8">
            <router-link :to="localeTo({ name: 'AboutUsPage' })" class="link">{{ t('nav.about') }}</router-link>
            <router-link :to="localeTo({ name: 'BlogPage' })" class="link">{{ t('nav.blog') }}</router-link>
            <router-link :to="localeTo({ name: 'FaqPage' })" class="link">{{ t('nav.faq') }}</router-link>
            <router-link :to="localeTo({ name: 'ContactUsPage' })" class="link">{{ t('nav.contact') }}</router-link>
          </div>

          <button class="flex items-center gap-2 link" @click="localeModal = true">
            <img :src="getFlagUrl(selectedCountry)" :alt="selectedCountry" class="w-5 h-auto rounded-sm" />
            <span class="uppercase text-xs font-medium">{{ locale }}</span>
          </button>
        </div>
      </div>
    </div>

    <div class="max-w-7xl mx-auto px-4 py-4 md:py-2.5 flex items-center gap-6 justify-between">
      <router-link :to="localeTo({ name: 'HomePage' })" class="flex-shrink-0">
        <img :src="logo" alt="GMG Automation" class="h-6 w-auto" />
      </router-link>

      <nav class="hidden lg:flex items-center gap-6 font-medium flex-1">
        <router-link v-for="item in navItems" :key="item.key" :to="localeTo(item.to)" class="link">
          {{ t(item.key) }}
        </router-link>
      </nav>

      <div class="hidden lg:flex items-center gap-6">
        <div ref="searchWrapperRef" class="relative">
          <IconField>
            <InputText
              ref="searchInputRef"
              v-model="searchQuery"
              :placeholder="t('header.searchPlaceholder')"
              size="small"
              class="!bg-white/10 !border-white/20 !text-gray-50 !placeholder-gray-400 w-[22rem]"
              autocomplete="off"
              autocorrect="off"
              autocapitalize="off"
              spellcheck="false"
              @focus="onSearchFocus"
              @input="onSearchInput"
              @keydown.enter="onSearchEnter(searchInputRef)"
              @keydown.esc="closeDropdown"
            />
            <InputIcon class="pi pi-search" />
          </IconField>

          <Transition name="dropdown">
            <SearchDropdown
              v-if="dropdownOpen"
              :results="searchResults"
              :loading="searchLoading"
              :query="searchQuery"
              :min-length="MIN_LENGTH"
              @select-product="goToProduct($event, searchInputRef)"
              @select-enquiry="goToEnquiry()"
            />
          </Transition>
        </div>

        <router-link :to="localeTo({ name: 'CartPage' })" class="relative p-1 link">
          <i class="pi pi-shopping-cart text-2xl"></i>
          <span
            class="absolute -top-1 -right-1 bg-[#2db9d6] text-[#041E42] text-xs rounded-full w-5 h-5 flex items-center justify-center leading-none"
          >
            {{ cartStore.count }}
          </span>
        </router-link>

        <AppButton :label="t('nav.getQuote')" @click="quoteDrawerOpen = true">
          <template #icon><FileText class="w-5 h-5" /></template>
        </AppButton>
      </div>

      <div class="flex lg:hidden items-center gap-4">
        <button class="flex items-center gap-2 link" @click="localeModal = true">
          <img :src="getFlagUrl(selectedCountry)" :alt="selectedCountry" class="w-6 h-auto rounded-sm" />
          <span class="uppercase text-sm font-medium">{{ locale }}</span>
        </button>

        <router-link :to="localeTo({ name: 'CartPage' })" class="relative p-1 link">
          <i class="pi pi-shopping-cart text-2xl"></i>
          <span
            class="absolute -top-1 -right-1 bg-[#2db9d6] text-[#041E42] text-xs rounded-full w-5 h-5 flex items-center justify-center leading-none"
          >
            {{ cartStore.count }}
          </span>
        </router-link>

        <Button icon="pi pi-bars text-2xl" severity="secondary" variant="text" rounded @click="mobileOpen = true" />
      </div>
    </div>
  </header>

  <Transition name="fade">
    <div v-if="mobileOpen" class="fixed inset-0 bg-black/50 z-50 lg:hidden" @click="mobileOpen = false" />
  </Transition>

  <Transition name="slide-right">
    <div v-if="mobileOpen" class="fixed top-0 right-0 h-full w-full bg-white z-50 lg:hidden flex flex-col shadow-2xl">
      <div class="flex items-center justify-between px-5 py-4 bg-[#041E42]">
        <img :src="logo" alt="GMG Automation" class="h-5 w-auto" />
        <Button
          icon="pi pi-times text-2xl"
          severity="secondary"
          variant="text"
          rounded
          class="!text-gray-50"
          @click="mobileOpen = false"
        />
      </div>

      <nav class="flex flex-col px-5 py-6 gap-4 font-medium text-[#1a233f] flex-1 overflow-y-auto">
        <router-link
          v-for="item in allNavItems"
          :key="item.key"
          :to="localeTo(item.to)"
          class="py-3 px-3 rounded-lg hover:bg-gray-100 transition-colors text-[#1a233f]"
          :class="isNavItemActive(item) ? '!text-[#2db9d6] !bg-[#2db9d6]/5 !font-semibold' : ''"
          @click="mobileOpen = false"
        >
          {{ t(item.key) }}
        </router-link>
      </nav>

      <div class="px-5 py-5 border-t border-gray-200">
        <AppButton
          :label="t('nav.getQuote')"
          size="large"
          class="w-full justify-center"
          @click="
            () => {
              mobileOpen = false
              quoteDrawerOpen = true
            }
          "
        >
          <template #icon><FileText class="w-5 h-5" /></template>
        </AppButton>
      </div>
    </div>
  </Transition>

  <LocaleModal v-model="localeModal" v-model:selected-flag="selectedFlag" v-model:selected-country="selectedCountry" />
  <QuoteDrawer v-model="quoteDrawerOpen" />
</template>

<script setup>
import { computed, nextTick, onMounted, onUnmounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { IconField, InputIcon, InputText } from 'primevue'
import LocaleModal from '@/components/general/LocaleModal.vue'
import { useLocaleStore } from '@/stores/general/localeStore'
import { getFlagUrl } from '@/utils/helpers'
import { FileText } from 'lucide-vue-next'
import AppButton from '@/components/general/ui/AppButton.vue'
import SearchDropdown from '@/components/general/ui/SearchDropdown.vue'
import { usePartSearch } from '@/composables/usePartSearch'
import QuoteDrawer from '@/components/general/ui/QuoteDrawer.vue'
import { useCartStore } from '@/stores/general/cartStore'
import { useRoute } from 'vue-router'
import { useLocaleTo } from '@/composables/useLocaleTo'

const logo = new URL('@/assets/images/logo_mini.svg', import.meta.url).href

const { t, locale } = useI18n({ useScope: 'global' })
const { localeTo } = useLocaleTo()
const mobileOpen = ref(false)
const localeModal = ref(false)
const headerRef = ref(null)
const localeStore = useLocaleStore()
const cartStore = useCartStore()
const route = useRoute()

const quoteDrawerOpen = ref(false)

const selectedFlag = computed(() => localeStore.flag)
const selectedCountry = computed(() => localeStore.country)

const searchInputRef = ref(null)
const searchWrapperRef = ref(null)

const {
  MIN_LENGTH,
  searchQuery,
  searchResults,
  searchLoading,
  dropdownOpen,
  onSearchInput,
  onSearchFocus,
  closeDropdown,
  goToProduct,
  goToEnquiry,
  onSearchEnter,
  destroy,
} = usePartSearch()

const showTopBar = ref(true)
const topBarRef = ref(null)
const topBarHeight = ref(0)
let lastScrollY = 0

const navItems = [
  { key: 'nav.home', to: { name: 'HomePage' } },
  { key: 'nav.manufacturers', to: { name: 'ManufacturersPage' } },
]

const allNavItems = [
  { key: 'nav.home', to: { name: 'HomePage' } },
  { key: 'nav.manufacturers', to: { name: 'ManufacturersPage' } },
  { key: 'nav.about', to: { name: 'AboutUsPage' } },
  { key: 'nav.blog', to: { name: 'BlogPage' } },
  { key: 'nav.faq', to: { name: 'FaqPage' } },
  { key: 'nav.contact', to: { name: 'ContactUsPage' } },
]

const onClickOutside = (e) => {
  if (searchWrapperRef.value && !searchWrapperRef.value.contains(e.target)) {
    closeDropdown()
  }
}

function onScroll() {
  const currentScrollY = window.scrollY
  showTopBar.value = currentScrollY < lastScrollY || currentScrollY < 60
  lastScrollY = currentScrollY
}

const CHILD_ROUTES = {
  ManufacturersPage: ['ManufacturerProductsPage'],
  BlogPage: ['BlogItemPage'],
}

const isNavItemActive = (item) => {
  const name = item.to.name
  return route.name === name || (CHILD_ROUTES[name] ?? []).includes(route.name)
}

onMounted(async () => {
  await nextTick()
  topBarHeight.value = topBarRef.value?.offsetHeight ?? 0

  const updateHeaderHeight = () => {
    const h = headerRef.value?.offsetHeight ?? 0
    document.documentElement.style.setProperty('--header-height', `${h}px`)
  }

  updateHeaderHeight()
  window.addEventListener('resize', updateHeaderHeight)
  window.addEventListener('scroll', onScroll, { passive: true })
  document.addEventListener('mousedown', onClickOutside)
})

onUnmounted(() => {
  window.removeEventListener('scroll', onScroll)
  window.removeEventListener('resize', () => {})
  document.removeEventListener('mousedown', onClickOutside)
  destroy()
})
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.slide-right-enter-active,
.slide-right-leave-active {
  transition: transform 0.3s ease;
}
.slide-right-enter-from,
.slide-right-leave-to {
  transform: translateX(100%);
}

.dropdown-enter-active,
.dropdown-leave-active {
  transition:
    opacity 0.15s ease,
    transform 0.15s ease;
}
.dropdown-enter-from,
.dropdown-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}
</style>
